import { authFetch, apiErrorMessage } from './authFetch';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

export interface CrmCatalogRefreshResult {
    crm: 'ghl' | 'av_emr';
    providers_upserted: number;
    providers_inactive: number;
    providers_retired: number;
    services_upserted: number;
    services_inactive: number;
    services_retired: number;
    skipped: string[];
}

export interface GhlCleanupPreview {
    gh_id: string;
    crm: 'av_emr';
    total: number;
    engine_total: number;
    calendar_associations: Array<{
        id: number;
        emr_integration_id: number;
        emr: string | null;
        direction: string | null;
        calendar_ghl_id: string | null;
        calendar_name: string | null;
        emr_appointment_data_assoc_id: number | null;
    }>;
    providers: Array<{
        id: number;
        emr_integration_id: number;
        emr_id: string | null;
        name: string | null;
        ghl_id: string | null;
        ghl_user_name: string | null;
    }>;
    provider_calendars: Array<{
        id: number;
        emr_integration_id: number;
        name: string | null;
        calendar_ghl_id: string | null;
        calendar_name: string | null;
    }>;
    default_calendars: Array<{
        emr_integration_id: number;
        emr: string;
        calendar_ghl_id: string;
        calendar_name: string | null;
    }>;
    default_providers: Array<{
        emr_integration_id: number;
        emr: string;
        ghl_user_id: string;
        ghl_user_name: string | null;
    }>;
    association_default_providers: Array<{
        id: number;
        emr_integration_id: number;
        emr: string | null;
        calendar_ghl_id: string | null;
        calendar_name: string | null;
        ghl_user_id: string;
        ghl_user_name: string | null;
    }>;
    pending_suggestions: {
        practitioner: number;
        appointment_type: number;
        practitioner_no_match: number;
        appointment_type_no_match: number;
    };
    catalog_first_loaded_at: string | null;
    restorable_archive_rows: number;
}

export interface GhlCleanupResult {
    gh_id: string;
    batch_id: string | null;
    archived: number;
    removed: {
        calendar_associations: number;
        providers: number;
        provider_calendars: number;
        default_calendars: number;
        default_providers: number;
        association_default_providers: number;
    };
    suggestions_dismissed: { practitioner: number; appointment_type: number };
    restorable_archive_rows: number;
}

export interface GhlArchiveSummary {
    gh_id: string;
    crm: 'ghl' | 'av_emr';
    restorable_archive_rows: number;
    by_table: Record<string, number>;
    last_archived_at: string | null;
    can_restore: boolean;
}

export interface GhlRestoreResult {
    gh_id: string;
    processed: number;
    restored: { engine: number; suggestions: number };
    skipped: Array<{ table: string; row_id: number; reason: string }>;
}

async function jsonOrThrow<T>(response: Response, fallback: string): Promise<T> {
    if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(apiErrorMessage(body, fallback));
    }
    return response.json();
}

const cleanupUrl = (ghId: string) => `${API_BASE_URL}/crm-catalog/${encodeURIComponent(ghId)}/ghl-cleanup`;

export const crmCatalogApi = {
    /** Re-read the AV EMR providers/services for an AV EMR practice and cache them as mapping targets. */
    async refreshAvEmrCatalog(ghId: string): Promise<CrmCatalogRefreshResult> {
        const response = await authFetch(
            `${API_BASE_URL}/crm-catalog/${encodeURIComponent(ghId)}/refresh`,
            { method: 'POST' },
        );
        if (!response.ok) {
            const body = await response.json().catch(() => null);
            throw new Error(apiErrorMessage(body, 'Failed to refresh the AV EMR catalog'));
        }
        return response.json();
    },

    /** Old GHL mappings of an AV EMR practice (read-only preview). 409 for GHL practices. */
    async previewGhlCleanup(ghId: string): Promise<GhlCleanupPreview> {
        const response = await authFetch(cleanupUrl(ghId));
        return jsonOrThrow(response, 'Failed to load old GHL mappings');
    },

    /** Archive, then remove the old GHL mappings of an AV EMR practice. */
    async applyGhlCleanup(ghId: string): Promise<GhlCleanupResult> {
        const response = await authFetch(cleanupUrl(ghId), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ confirm: true }),
        });
        return jsonOrThrow(response, 'Failed to clean up old GHL mappings');
    },

    /** Archived GHL mappings that can be restored (any CRM). */
    async getGhlArchive(ghId: string): Promise<GhlArchiveSummary> {
        const response = await authFetch(`${cleanupUrl(ghId)}/archive`);
        return jsonOrThrow(response, 'Failed to load archived GHL mappings');
    },

    /** Put archived GHL mappings back (practice must be on GHL again). */
    async restoreGhlMappings(ghId: string): Promise<GhlRestoreResult> {
        const response = await authFetch(`${cleanupUrl(ghId)}/restore`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ confirm: true }),
        });
        return jsonOrThrow(response, 'Failed to restore GHL mappings');
    },
};
